import { Link } from "react-router";
import { ComparePage } from "./ComparePage";

// Angle: GERIATRIC CLINICAL DEPTH. The reader cares about how well a platform
// supports geriatric prescribing review (PIMs, anticholinergic load,
// psychotropic GDR). Nothing here describes GeriMed itself.
export function GerimedAlternative() {
  return (
    <ComparePage
      slug="gerimed-alternative"
      competitor="GeriMed"
      title="GeriMed Alternative for Consultant Pharmacists | WeConsult Rx"
      description="Evaluating a GeriMed alternative? WeConsult Rx gives consultant pharmacists Beers, STOPP, anticholinergic-burden and GDR checks, plus e-sign and invoicing, in one platform."
      h1="A GeriMed alternative with geriatric prescribing review at its core."
      eyebrow="GeriMed alternative"
      answer="Searching for a GeriMed alternative? WeConsult Rx is consultant pharmacy software that screens every long-term-care resident's regimen against geriatric criteria (Beers, STOPP, anticholinergic burden, and gradual dose reduction) and turns each finding into a pharmacist-approved, prescriber-signed recommendation. The same platform then bills the facility for the review."
      criteriaHeading="What geriatric medication review software should do"
      criteriaLede="Older adults in long-term care carry the highest regimen risk. These are the capabilities worth testing on your own charts."
      criteria={[
        { label: "Potentially inappropriate medications", detail: "Beers and STOPP criteria are applied to every resident each month, with the rationale drafted for you to confirm or edit." },
        { label: "Duplicate and additive risk", detail: "Therapeutic duplications and additive same-class risk are caught across the whole active list, including long-term benzodiazepine use." },
        { label: "Cumulative anticholinergic load", detail: "Anticholinergic burden is scored across the whole regimen, so additive risk is visible even when no single drug trips a flag." },
        { label: "Gradual dose reduction history", detail: "GDR attempts for psychotropics are tracked over time, which is the record surveyors ask for under F605." },
        { label: "Look-alike, sound-alike safety", detail: "Look-alike, sound-alike drug pairs on the regimen are flagged so nursing can be alerted alongside the recommendation." },
        { label: "Judgment stays with the pharmacist", detail: "Rules surface and AI drafts; the consultant pharmacist decides and signs. Nothing reaches a prescriber unreviewed." },
      ]}
      sections={[
        {
          heading: "From a geriatric flag to a signed change",
          body: (
            <>
              <p>
                A flag is only useful if it becomes a decision. In WeConsult Rx each finding moves through the same
                short path:
              </p>
              <ul>
                <li><strong>Flag.</strong> A rule pack identifies, for example, a sedative-hypnotic in a resident with a fall history.</li>
                <li><strong>Recommendation.</strong> AI drafts the clinical rationale with its criteria reference; you edit and approve it.</li>
                <li><strong>Response.</strong> The prescriber accepts, modifies, or declines with a reason, and signs electronically.</li>
                <li><strong>Record.</strong> The outcome is stored with the date and signer, ready for the next review and for survey.</li>
              </ul>
            </>
          ),
        },
        {
          heading: "Psychotropics, GDR, and F605",
          body: (
            <>
              <p>
                Psychotropic review is where geriatric documentation is most often questioned. WeConsult Rx keeps each
                resident's GDR attempts, clinical contraindications, and prescriber responses together, so the history
                is one view rather than a search through past months.
              </p>
              <p>
                For a template your team can use today, see the{" "}
                <Link to="/resources/gdr-documentation-template">GDR documentation template</Link>, and the{" "}
                <Link to="/resources/f-tag-readiness-checklist">F-tag readiness checklist</Link> for the wider survey picture.
              </p>
            </>
          ),
        },
      ]}
      faqs={[
        {
          q: "What is a good alternative to GeriMed consultant pharmacy software?",
          a: "WeConsult Rx is an alternative focused on geriatric medication review for long-term care: Beers, STOPP, anticholinergic burden, and GDR checks on every resident, with prescriber and DON e-signature and facility invoicing in the same platform.",
        },
        {
          q: "Does WeConsult Rx apply the Beers Criteria and STOPP?",
          a: "Yes. Both are part of the SNF rule pack and are applied to every resident's regimen each month. The pharmacist reviews each flag and finalizes the recommendation; criteria sets are updated as they are revised.",
        },
        {
          q: "How does WeConsult Rx support gradual dose reduction documentation?",
          a: "It tracks GDR attempts for psychotropic medications over time alongside prescriber responses and documented contraindications, giving surveyors a continuous record under F605.",
        },
        {
          q: "Is WeConsult Rx only for skilled nursing facilities?",
          a: "No. It has separate rule packs for skilled nursing, ambulatory surgery centers, and dialysis facilities, so consultant pharmacists with a mixed client list work in one system.",
        },
        {
          q: "Does the software make clinical decisions?",
          a: "No. Rules surface potential issues and AI drafts the rationale, but the consultant pharmacist reviews, edits, and signs every recommendation before a prescriber sees it.",
        },
      ]}
      related={[
        { to: "/solutions/snf", label: "Long-term care (SNF)", desc: "The SNF rule pack and monthly review." },
        { to: "/resources/gdr-documentation-template", label: "GDR documentation template", desc: "A free template for psychotropic GDR notes." },
        { to: "/consultant-pharmacist-software", label: "Consultant pharmacist software", desc: "One platform for SNF, ASC, and dialysis." },
      ]}
      cta={{
        title: "Run your geriatric review on sample charts",
        body: "See the Beers, STOPP, anticholinergic, and GDR checks on a synthetic facility, or start with a free first-facility review.",
      }}
    />
  );
}
